import { BUILD_VERSION } from '@data/build-version.js';

/**
 * Service responsible for loading and managing the search indexes and text data
 */
export const databaseService = {
  indexes: null,
  allTokens: null,
  text: null,
  loadPromise: null,

  /**
   * Loads the search indexes and text data from the server
   * Uses a promise to ensure the data is only loaded once
   * @returns {Promise} Promise that resolves with the indexes and text data
   */
  async loadDatabase() {
    if (this.indexes && this.text) {
      return { indexes: this.indexes, text: this.text };
    }
    
    // If there's already a promise, return it. Atomic-like operation.
    if (this.loadPromise) {
      return this.loadPromise;
    }
    
    // Set a promise first, an "atomic" operation
    this.loadPromise = (async () => {
      try {
        console.log('Fetching index and text from server...');

        const [indexesResponse, textResponse] = await Promise.all([
          fetch(`/data/urantia-papers-indexes.json?v=${BUILD_VERSION}`),
          fetch(`/data/urantia-papers.json?v=${BUILD_VERSION}`)
        ]);
        
        // Process responses...
        this.indexes = await indexesResponse.json();
        this.text = await textResponse.json();

        // Build a list of all tokens just once, for use by all methods
        this.allTokens = Object.keys(this.indexes.tokenToParagraphId);

        console.log('Successfully loaded indexes and text.');
        
        return { indexes: this.indexes, text: this.text };
      } catch (error) {
        // Important: clear the promise on error so we can try again
        this.loadPromise = null;
        throw error;
      }
    })();
    
    return this.loadPromise;
  },

  /**
   * Gets a paragraph by its ID
   * @param {number} id - The paragraph ID
   * @returns {Object} The paragraph data
   */
  getParagraphTextById(id) {
    return this.text?.[id];
  },

  /**
   * Gets the number of sections in a paper
   * @param {number} paperNumber - The paper number
   * @returns {number} The number of sections in the paper
   */
  getNumSectionsInPaper(paperNumber) {
    if (!this.text) return 0;
    
    // Get the section numbers from paragraphs matching the paper number
    const sectionNumbers = Object.values(this.text)
      .filter(paragraph => {
        const match = paragraph.psp.match(/^(\d+):(\d+)\.(\d+)$/);
        return match && parseInt(match[1]) === paperNumber;
      })
      .map(paragraph => {
        const match = paragraph.psp.match(/^(\d+):(\d+)\.(\d+)$/);
        return parseInt(match[2]);
      });
    
    // If no sections found, return 0
    if (sectionNumbers.length === 0) return 0;
    
    // Find the max section number
    return Math.max(...sectionNumbers);
  },

  /**
   * Gets the number of paragraphs in a specific paper section
   * @param {number} paperNumber - The paper number
   * @param {number} sectionNumber - The section number
   * @returns {number} The number of paragraphs in the section
   */
  getNumParagraphsInSection(paperNumber, sectionNumber) {
    if (!this.text) return 0;
    
    // Count paragraphs matching both paper and section number
    const paragraphNumbers = Object.values(this.text)
      .filter(paragraph => {
        const match = paragraph.psp.match(/^(\d+):(\d+)\.(\d+)$/);
        return match && 
               parseInt(match[1]) === paperNumber && 
               parseInt(match[2]) === sectionNumber;
      })
      .map(paragraph => {
        const match = paragraph.psp.match(/^(\d+):(\d+)\.(\d+)$/);
        return parseInt(match[3]);
      });
    
    // If no paragraphs found, return 0
    if (paragraphNumbers.length === 0) return 0;
    
    // Find the max paragraph number
    return Math.max(...paragraphNumbers);
  },

  getParagraphIdForPaperSectionParagraph(paperNumber, sectionNumber, paragraphNumber) {
    if (!this.text) return 0;

    // Find the first paragraph ID that matches the paper, section, and paragraph numbers
    for (const [id, paragraph] of Object.entries(this.text)) {
      const match = paragraph.psp.match(/^(\d+):(\d+)\.(\d+)$/);
      if (match && 
          parseInt(match[1]) === paperNumber && 
          parseInt(match[2]) === sectionNumber &&
          parseInt(match[3]) === paragraphNumber) {
        return parseInt(id);
      }
    }
    
    // If no paragraphs found, return 0
    return 0;
  },
  
  /**
   * Gets the paragraph IDs that contain a specific token
   * @param {string} token - The token to look up
   * @returns {Array} Array of paragraph IDs
   */
  getParagraphIdsForToken(token) {
    const rawResults = this.indexes?.tokenToParagraphId?.[token];
    
    // Handle case where no results are found
    if (!rawResults) {
      return [];
    }
    
    // Convert delta-encoded results to regular array if necessary
    if (typeof rawResults === 'object' && !Array.isArray(rawResults) && rawResults.type === 'delta') {
      // We have delta-encoded results, need to decode
      const results = [rawResults.values[0]]; // Start with first value which is stored as-is
      
      // Add each delta to the previous value to get the actual paragraph IDs
      for (let i = 1; i < rawResults.values.length; i++) {
        results.push(results[i-1] + rawResults.values[i]);
      }
      return results;
    } 
    
    // Regular array format, use as-is
    return rawResults;
  },

  getTokensByPrefix(prefix) {
    return this.allTokens.filter(token => token.startsWith(prefix));
  },

  getTokensBySuffix(suffix) {
    return this.allTokens.filter(token => token.endsWith(suffix));
  },

  getTokensBySubstring(substring) {
    return this.allTokens.filter(token => token.includes(substring));
  },

  /**
   * Checks if a token exists in the database
   * @param {string} token - The token to check
   * @returns {boolean} True if the token exists, false otherwise
   */
  hasToken(token) {
    return token in this.indexes?.tokenToParagraphId;
  }

};

export default databaseService;